import type { ReactNode } from 'react';
import { motion } from 'framer-motion';

interface Technology {
  name: string;
  category: string;
  logo: ReactNode;
}

const technologies: Technology[] = [
  {
    name: 'AWS',
    category: 'Cloud',
    logo: (
      <svg viewBox="0 0 100 60" className="w-16 h-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
        <text y="30" fontSize="13" fill="#FF9900" fontFamily="Arial, sans-serif" fontWeight="bold">Amazon</text>
        <text y="46" fontSize="13" fill="#FF9900" fontFamily="Arial, sans-serif" fontWeight="bold">Web Services</text>
      </svg>
    ),
  },
  {
    name: 'Python',
    category: 'Language',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="pyGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#387EB8" />
            <stop offset="100%" stopColor="#366994" />
          </linearGradient>
          <linearGradient id="pyGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFE052" />
            <stop offset="100%" stopColor="#FFC331" />
          </linearGradient>
        </defs>
        <path d="M24.05 2C13.4 2 14.1 6.7 14.1 6.7l.01 4.85H24.2V13H8.8S2 12.2 2 22.97c0 10.76 5.95 10.38 5.95 10.38h3.55v-4.99s-.19-5.95 5.86-5.95h10.1s5.67.09 5.67-5.48V7.67S34.03 2 24.05 2zm-5.6 3.25c1.02 0 1.84.82 1.84 1.84S19.47 8.93 18.45 8.93s-1.84-.82-1.84-1.84.82-1.84 1.84-1.84z" fill="url(#pyGrad1)" />
        <path d="M24.27 46C34.93 46 34.23 41.3 34.23 41.3l-.01-4.85H24.13V34H39.53S46.33 34.8 46.33 24.03c0-10.76-5.95-10.38-5.95-10.38H36.83v4.99s.19 5.95-5.86 5.95H20.87s-5.67-.09-5.67 5.48V40.33S14.3 46 24.27 46zm5.6-3.25c-1.02 0-1.84-.82-1.84-1.84s.82-1.84 1.84-1.84 1.84.82 1.84 1.84-.82 1.84-1.84 1.84z" fill="url(#pyGrad2)" />
      </svg>
    ),
  },
  {
    name: 'Terraform',
    category: 'IaC',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.5 5L5 12.25v14.5L17.5 34V19.5L30 12.25z" fill="#7B42BC" />
        <path d="M30.5 12.25L18 5v14.5L30.5 27V12.25z" fill="#7B42BC" opacity="0.7" />
        <path d="M31 14.5L18.5 21.75v14.5L31 43.5V29L43.5 21.75z" fill="#4040B2" />
        <path d="M43.5 14.5L31 21.75V36.25L43.5 29z" fill="#4040B2" opacity="0.7" />
      </svg>
    ),
  },
  {
    name: 'Claude AI',
    category: 'AI',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="20" fill="#D97757" />
        <text x="24" y="29" textAnchor="middle" fontSize="16" fill="white" fontFamily="Georgia, serif" fontWeight="bold">C</text>
      </svg>
    ),
  },
  {
    name: 'Kubernetes',
    category: 'Orchestration',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="20" fill="#326CE5" />
        <path d="M24 8l2.5 7.5h8l-6.5 4.5 2.5 7.5L24 23l-6.5 4.5 2.5-7.5-6.5-4.5h8z" fill="white" fillOpacity="0.9" />
      </svg>
    ),
  },
  {
    name: 'Docker',
    category: 'Containers',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <path d="M26 20h4v4h-4zM20 20h4v4h-4zM14 20h4v4h-4zM26 14h4v4h-4zM20 14h4v4h-4zM20 8h4v4h-4z" fill="#2396ED" />
        <path d="M44 22.5c-.5-2-2.5-3-4-3.5-.5-2.5-2.5-4-5-4.5l-.5.5c2 .5 3.5 2 3.5 4h-1c0-3-2.5-5-6-5-1 0-2 .5-2.5 1H28c0-.5.5-1 1-1H8.5C7 14 6 15 6 16.5c-2 .5-3.5 2.5-3.5 4.5 0 3.5 3 6.5 6.5 6.5h27c3 0 5.5-2 6-4.5.5 0 1.5-.5 2-1z" fill="#2396ED" />
      </svg>
    ),
  },
  {
    name: 'OpenAI',
    category: 'AI',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="20" fill="#10A37F" />
        <path d="M24 12c-1.5 0-2.8.6-3.8 1.5C19 14.6 18.5 16 18.5 17.5c-1.3.4-2.3 1.4-2.8 2.7-.3.8-.3 1.7 0 2.5-.9.7-1.5 1.8-1.5 3 0 1.2.6 2.3 1.5 3-.3.8-.3 1.7 0 2.5.5 1.3 1.5 2.3 2.8 2.7.1 1.5.5 2.8 1.7 3.8 1 .9 2.3 1.3 3.8 1.3s2.8-.5 3.8-1.3c1.1-1 1.6-2.3 1.7-3.8 1.3-.4 2.3-1.4 2.8-2.7.3-.8.3-1.7 0-2.5.9-.7 1.5-1.8 1.5-3 0-1.2-.6-2.3-1.5-3 .3-.8.3-1.7 0-2.5-.5-1.3-1.5-2.3-2.8-2.7C29.5 17.5 29 16 27.8 15 26.8 14 25.5 12 24 12z" fill="white" fillOpacity="0.9" />
      </svg>
    ),
  },
  {
    name: 'TypeScript',
    category: 'Language',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="48" rx="4" fill="#3178C6" />
        <path d="M22 22h-6v-3h16v3h-6v14h-4V22zm10 11c.5 1 1.5 1.5 2.5 1.5 1 0 1.7-.5 1.7-1.3 0-.9-.7-1.3-2-1.8L33 31c-2-.7-3.3-2-3.3-4 0-2.5 2-4.3 5-4.3 1.7 0 3.3.7 4.3 2l-2 2c-.5-.8-1.3-1.3-2.3-1.3-.9 0-1.5.5-1.5 1.2 0 .8.6 1.1 2 1.6l1.3.4c2.3.8 3.5 2 3.5 4.3 0 2.7-2.1 4.5-5.3 4.5-2.2 0-4-.9-5-2.5l2.3-1.9z" fill="white" />
      </svg>
    ),
  },
  {
    name: 'GitHub Actions',
    category: 'CI/CD',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="20" fill="#2088FF" />
        <path d="M24 8C15.2 8 8 15.2 8 24s7.2 16 16 16 16-7.2 16-16S32.8 8 24 8zm4 22h-2v-2h2v2zm0-4h-2v-8h2v8zm-6 4h-2v-2h2v2zm0-4h-2v-8h2v8z" fill="white" fillOpacity="0.9" />
      </svg>
    ),
  },
  {
    name: 'Node.js',
    category: 'Runtime',
    logo: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg">
        <path d="M24 4L6 14.5v19L24 44l18-10.5v-19L24 4z" fill="#339933" />
        <text x="24" y="29" textAnchor="middle" fontSize="10" fill="white" fontFamily="Arial, sans-serif" fontWeight="bold">Node</text>
      </svg>
    ),
  },
];

const allTech: Technology[] = [...technologies, ...technologies];

interface TechBadgeProps {
  tech: Technology;
}

function TechBadge({ tech }: TechBadgeProps) {
  return (
    <div
      className="flex-shrink-0 flex flex-col items-center justify-center gap-3 mx-3 bento-card group cursor-default select-none"
      style={{ width: '144px', height: '112px', minWidth: '144px' }}
    >
      <div className="flex items-center justify-center h-12 opacity-70 group-hover:opacity-100 transition-opacity duration-300">
        {tech.logo}
      </div>
      <div className="text-center">
        <div className="text-xs font-semibold text-slate-300">{tech.name}</div>
        <div className="text-[10px] text-slate-600 mt-0.5">{tech.category}</div>
      </div>
    </div>
  );
}

export default function TechStack() {
  return (
    <section id="tech-stack" className="relative py-24 lg:py-32 bg-black overflow-hidden">
      <div className="divider-glow mb-16" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-xs font-medium text-blue-400 uppercase tracking-widest mb-3">Our Arsenal</p>
          <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight mb-4">
            Built with best-in-class
            <br />
            <span className="gradient-text">tools & technologies.</span>
          </h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto leading-relaxed">
            We don't pick tools for novelty—we pick them for proven production reliability at scale.
          </p>
        </motion.div>
      </div>

      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />

        <div className="flex overflow-hidden w-full">
          <div className="marquee-track flex items-center flex-nowrap" style={{ width: 'max-content' }}>
            {allTech.map((tech, i) => (
              <TechBadge key={`${tech.name}-${i}`} tech={tech} />
            ))}
          </div>
        </div>
      </div>

      <div className="divider-glow mt-16" />
    </section>
  );
}
